import React, { useEffect, useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { get, isEmpty, isNil } from 'lodash';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import ClaimInformation from 'containers/PreOpeningPage/ClaimInformation';
import ClaimCircumstances from 'containers/PreOpeningPage/ClaimCircumstances';
import ClaimDeclaration from 'containers/PreOpeningPage/ClaimDeclaration';
import VictimCard from 'containers/PreOpeningPage/VictimCard';
import UploadDocumentsBloc from 'containers/RecapPage/uploadDocumentsBloc';
import { Listings } from 'containers/AppContainer/typings';
import {
  CLAIM_STATUS,
  EMPTY_CLAIM_TO_PREOPEN,
  VICTIM,
} from 'containers/data-structure';
import Breadcrumbs from 'containers/RecapManagerPage/Breadcrumbs';
import { claimStatusLabel } from 'containers/RecapManagerPage/utils';
import {
  GlobalBloc,
  Container,
} from 'containers/LawyerRecapPage/styled-component';
import OpposingPartyRecapContainer from 'containers/PreOpeningPage/OpposingParty/OpposingPartyRecapContainer';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import FraudeInformation from 'containers/PreOpeningPage/fraude';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import { CorporealDocument } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import {
  EMPTY_SUPPORT_DOC,
  PV_DOC_ID,
  SUPPORT_DOC_ID,
} from 'containers/LawyerRecapPage/constants';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import { BUTTON_COLORS, COLORS } from 'constants/design';
import { translate } from 'containers/services/translate';
import messages from 'containers/LawyerRecapPage/messages';
import { renderSearchQuery } from 'containers/ExpertAssignedMissionShow/utils';
import LawyerClaimCircumstances from 'containers/LawyerRecapPage/LawyerClaimCircumstances';
import SuspiciousCard from 'containers/CourtManagerLegalProceedings/SuspiciousCard';
import {
  CHOICE,
  INJURY_POST_DECEASED,
} from 'containers/PreOpeningPage/constants';
import {
  resolveSagisClaim,
  shouldShowVictimExcludedWarning,
} from 'helpers/utils';
import { rcCoporeal } from 'containers/CourtManagerLegalProceedings/constants';
import { formatOpposingParties } from 'containers/CourtManagerLegalProceedings/utils';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { extractOpposingParties } from 'containers/PreOpeningPage/utils';
import {
  PRIVILEGES,
  userHasPrivilege,
} from 'containers/services/security/user-privileges';
import WarningCard from 'containers/common/Card/WarningCard';
import {
  STATUS_PENDING,
  STATUS_VALIDATED,
} from 'containers/LawyerFeeNote/constants';
import { PROFESSION_CODES } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import { useSelector } from 'react-redux';
import {
  addPriorityReasonProfession,
  removeRejectedVictims,
  removeWhiteSpaceFromString,
} from 'services/utils';
import { VictimType } from 'typings/globalTypes';
import { ClaimRecapType } from 'typings/claimTypes';

export interface LawyerRecapProps {
  intl: IntlShape;
  listings: Listings;
  storedClaimToPreopen: any;
  requestClaimRecap: Function;
  claimId: number;
  claimRecap: ClaimRecapType;
  policyInfo: { sagis: {} };
  requestSearchClaims: Function;
  resetSearchClaimsStore: Function;
  requestSagisClaimInfo: Function;
  shouldDisplayBreadCrumbs: boolean;
  className: string;
  shouldNotGetClaim?: boolean;
  stakeholders: any;
  documentId?: string | undefined;
  setDocumentId?: Function;
  uploadDocument?: Function;
  uploadedDocument?: IUploadedDocument;
  resetUploadedDocument?: Function;
  requestAddDocumentClaim?: Function;
  requestUpdateVictimInfo?: Function;
  setClaimRecap: Function;
  requestUpdateBeneficiaryInfo?: Function;
  requestUpdateVehicleInfo?: Function;
  requestCreateVehicle?: Function;
  requestCreateVictim?: Function;
  requestUpdateClaim?: Function;
  isCourtManager?: boolean;
  sagisInfo: any;
  keycloakToken: any;
  isMonitoringManager?: boolean;
  requestAddEligibilityTransaction?: Function;
  requestAddTransaction?: Function;
  requestUpdateEligibilityTransaction?: Function;
  requestGetReportsPolicy: Function;
  requestGetDocByDocumentumId: Function;
  requestUpdateFraudInfo?: Function;
  transactionInboxeAreaId?: any;
}

const LawyerRecap: React.FC<LawyerRecapProps> = (props): JSX.Element => {
  const riskKey = get(
    resolveSagisClaim(props.policyInfo),
    'offeredRisks',
    [],
  ).find((element: { riskName: string }) => element.riskName === rcCoporeal)
    ?.riskId;
  const EditWarningMessage = styled.div`
    margin-bottom: 30px;
  `;
  const isRejected =
    get(props.claimRecap, 'claim.status') === CLAIM_STATUS.REJECTED &&
    !get(props.claimRecap, 'isOtherClaimsExist');
  useEffect(() => {
    if (!props.shouldNotGetClaim) props.requestClaimRecap(props.claimId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.claimId, props.keycloakToken]);

  const priorityReasonClaimId = useSelector((state: any) =>
    get(state.toJS(), ['recap', 'claimRecap', 'claim', 'priorityReasonId']),
  );

  const updatePriorityVictim =
    !removeRejectedVictims(
      get(props.claimRecap, 'victims', []),
    ).find((v: any) => PROFESSION_CODES.includes(v.professionCode)) &&
    priorityReasonClaimId === 5;

  const [dataClaimToUpdate, setDataClaimToUpdate] = useState<any>({});
  const claimResponsabilityRate =
    props.policyInfo &&
    get(resolveSagisClaim(props.policyInfo), 'offeredRisks', []).find(
      (element: { riskName: string }) => element.riskName === rcCoporeal,
    )?.responsibilityRate;
  const formattedSagisVictims = [...get(dataClaimToUpdate, 'victims', [])]
    .filter((v: any) => !v.isDeleted && !v.id && !v.victimKey)
    .map((victim: VictimType) => {
      let setDefaultBirthDate = false;
      if (isEmpty(victim[VICTIM.DATE_OF_BIRTH])) {
        setDefaultBirthDate = true;
      }
      if (victim.injuryPostKey === INJURY_POST_DECEASED) {
        victim.nextOfKins = victim.beneficiaries.map((el: any) => {
          return {
            fullName: `${removeWhiteSpaceFromString(
              el.firstName,
            )} ${removeWhiteSpaceFromString(el.lastName)}`,
            qualityCode: Number(el.beneficiaryType),
            age: el.age,
            birthDate: el?.dateOfBirth,
            funeralFees: featureService.hasFeature(FEATURES.NEW_LAW_DAHIR)
              ? el.funeralFees
              : !isNil(el.funeralFees) && !!Number(claimResponsabilityRate)
              ? el.funeralFees / (Number(claimResponsabilityRate) / 100)
              : el.funeralFees,
            handicapIndicator: el.handicapIndicator ? 'Y' : 'N',
            dependentIndicator: el.dependentIndicator ? 'Y' : 'N',
          };
        });
      }
      return {
        ...victim,
        id: undefined,
        victimFirstName: victim[VICTIM.FIRST_NAME],
        victimLastName: victim[VICTIM.LAST_NAME],
        victimBirthDate: victim[VICTIM.DATE_OF_BIRTH],
        injuryCode:
          String(victim[VICTIM.INJURY_POST_KEY]) === INJURY_POST_DECEASED
            ? victim[VICTIM.INJURY_POST_KEY]
            : 0,
        qualityVictim: victim[VICTIM.QUALITY_KEY],
        classVictim: victim[VICTIM.CLASS_KEY],
        Town: victim[VICTIM.CITY],
        isExcluded: false,
        cspCode: get(
          get(props.listings, 'Profession', []).find(
            (el: any) => el.ItemKey === victim.professionCode,
          ),
          'ItemGroup',
          null,
        ),
        gender: !isNil(victim[VICTIM.GENDER]) ? victim[VICTIM.GENDER] : '',
        Complement: victim[VICTIM.ADDRESS],
        isWorkAccident: victim[VICTIM.WORK_ACCIDENT_ID] === 1 ? true : false,
        setDefaultBirthDate,
        itt: parseInt((victim[VICTIM.ITT] as unknown) as string),
      };
    });

  const [isFistUpdate, setIsFistUpdate] = useState<boolean>(true);

  const victimKeys = get(props.claimRecap, 'victims', []).map(
    (el: any) => el?.victimKey,
  );
  useEffect(() => {
    if (!isEmpty(props.claimRecap) && isFistUpdate) {
      setDataClaimToUpdate({
        ...props.claimRecap,
        victims: featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO)
          ? [
              ...get(props.claimRecap, 'victims', []),
              ...get(props.sagisInfo, 'victims.Victims', []).filter(
                (el: any) => !victimKeys.includes(el?.VictimKey),
              ),
            ]
          : [...get(props.claimRecap, 'victims', [])],
      });
      setIsFistUpdate(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.claimRecap, props.sagisInfo]);

  useDeepCompareEffect(() => {
    props.resetSearchClaimsStore();
    if (!isEmpty(props.claimRecap)) {
      const searchQuery = renderSearchQuery(props.claimRecap);
      props.requestSearchClaims(searchQuery);
      if (
        featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO) &&
        get(props.claimRecap, 'claim.status', '') === CLAIM_STATUS.VALIDATED
      ) {
        props.requestSagisClaimInfo(
          get(props.claimRecap, 'claim.claimNumber', null),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.claimRecap, props.keycloakToken]);

  const getClaimStatus = () => {
    const claim = get(props.claimRecap, 'claim', {});
    const status = get(claim, 'status', '');
    return claimStatusLabel(status, props);
  };

  const getClaimNumber = () => {
    const claim = get(props.claimRecap, 'claim', {});
    return get(claim, 'claimNumber', '');
  };

  const [supportDocument, setSupportDocument] = useState({
    ...EMPTY_SUPPORT_DOC,
  });
  const [updatedPVDocument, setUpdatedPVDocument] = useState({
    ...EMPTY_SUPPORT_DOC,
  });

  const setDocument = (document: CorporealDocument) => {
    if (props.documentId === SUPPORT_DOC_ID) {
      setSupportDocument(document);
      setDataClaimToUpdate({
        ...dataClaimToUpdate,
        supportDocument: { ...document },
      });
      if (props.requestAddDocumentClaim)
        props.requestAddDocumentClaim({
          ...document,
          claimId: props.claimId,
          claimNumber: getClaimNumber(),
          isInboxesActive: featureService.hasFeature(
            FEATURES.ATTRIBUTION_INBOXES_MANAGER,
          ),
          status:
            props.isCourtManager || props.isMonitoringManager
              ? STATUS_VALIDATED
              : STATUS_PENDING,
        });
      if (props.resetUploadedDocument) props.resetUploadedDocument();
      props.requestClaimRecap(props.claimId);
    }
  };

  const successCallback = () => {
    toast.success(translate(messages.validationSuccessMessage, props));
  };

  const setPVDocument = (document: CorporealDocument) => {
    if (props.documentId === PV_DOC_ID) {
      setUpdatedPVDocument(document);
      if (props.resetUploadedDocument) props.resetUploadedDocument();
    }
  };

  const handleClickUpdateData = () => {
    props.requestUpdateClaim &&
      props.requestUpdateClaim(
        {
          ...dataClaimToUpdate,
          driver: !isEmpty(get(dataClaimToUpdate, 'driver'))
            ? {
                ...get(dataClaimToUpdate, 'driver'),
                isDriverTheInsured:
                  get(dataClaimToUpdate, 'driver.isDriverTheInsured') ===
                  CHOICE.NO
                    ? false
                    : Boolean(
                        get(dataClaimToUpdate, 'driver.isDriverTheInsured'),
                      ),
              }
            : {},
          claim: !props.isCourtManager
            ? {
                ...get(dataClaimToUpdate, 'claim'),
                status: CLAIM_STATUS.PENDING,
                priorityReasonId: addPriorityReasonProfession(
                  priorityReasonClaimId,
                  !!removeRejectedVictims(
                    get(props.claimRecap, 'victims', []),
                  ).find((v: any) =>
                    PROFESSION_CODES.includes(v.professionCode),
                  ),
                  props.listings,
                )
                  ? addPriorityReasonProfession(
                      priorityReasonClaimId,
                      !!removeRejectedVictims(
                        get(props.claimRecap, 'victims', []),
                      ).find((v: any) =>
                        PROFESSION_CODES.includes(v.professionCode),
                      ),
                      props.listings,
                    )
                  : updatePriorityVictim && priorityReasonClaimId != 4
                  ? null
                  : undefined,
              }
            : {
                ...get(dataClaimToUpdate, 'claim'),
                fraudInfo: get(props.claimRecap, 'claim.fraudInfo', []),
                priorityReasonId: addPriorityReasonProfession(
                  priorityReasonClaimId,
                  !!removeRejectedVictims(
                    get(props.claimRecap, 'victims', []),
                  ).find((v: any) =>
                    PROFESSION_CODES.includes(v.professionCode),
                  ),
                  props.listings,
                )
                  ? addPriorityReasonProfession(
                      priorityReasonClaimId,
                      !!removeRejectedVictims(
                        get(props.claimRecap, 'victims', []),
                      ).find((v: any) =>
                        PROFESSION_CODES.includes(v.professionCode),
                      ),
                      props.listings,
                    )
                  : updatePriorityVictim && priorityReasonClaimId != 4
                  ? null
                  : undefined,
              },
          isLawyer: !props.isCourtManager,
          isInboxesActive: featureService.hasFeature(
            FEATURES.ATTRIBUTION_INBOXES_MANAGER,
          ),
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
          victimsSagisToAdd: props.isCourtManager
            ? [...formattedSagisVictims]
            : undefined,
          opposingPartiesSagisToAdd: props.isCourtManager
            ? formatOpposingParties(
                get(dataClaimToUpdate, 'vehicules', []).filter(
                  (v: any) => !v.isDeleted && !v.id,
                ),
                riskKey,
                getClaimNumber(),
                props.claimId,
                get(props.listings, 'compagnies'),
              )
            : undefined,
          riskKey,
          claimNumber: get(dataClaimToUpdate, 'claim.claimNumber'),
        },
        props.claimId,
        successCallback,
      );
  };

  return (
    <GlobalBloc className={props.className}>
      {props.shouldDisplayBreadCrumbs && (
        <>
          <Breadcrumbs
            claimStatus={getClaimStatus()}
            claimNumber={getClaimNumber()}
          />
          {get(props.claimRecap['claim'], ['suspicious']) &&
            featureService.hasFeature(FEATURES.FRAUDE_CORPO) && (
              <SuspiciousCard />
            )}
          {shouldShowVictimExcludedWarning(
            get(props.claimRecap, 'victims'),
            featureService.hasFeature(
              FEATURES.IS_VICTIM_EXCLUDED_FROM_COVERAGE,
            ),
          ) && (
            <WarningCard
              message={messages.victimExcludedFromCoverageWarningMessage}
            />
          )}
        </>
      )}
      {get(props.claimRecap, 'claim.status') === CLAIM_STATUS.REJECTED &&
        get(props.claimRecap, 'isOtherClaimsExist') && (
          <EditWarningMessage>
            <span style={{ color: COLORS.RED }}>
              {translate(messages.editWarningMessage, props)}
            </span>
          </EditWarningMessage>
        )}
      {!isEmpty(props.claimRecap) && !isEmpty(props.policyInfo) && (
        <Container>
          <ClaimDeclaration
            isRecapPage
            isReadOnly
            isManager={false}
            inputProps={{ disableUnderline: true }}
            disableInput
            inputLabelProps
            listings={props.listings}
            claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
            storedClaimToPreopen={props.claimRecap}
            policyInfo={props.policyInfo}
            setClaimRecap={props.setClaimRecap}
            setDataClaimToUpdate={setDataClaimToUpdate}
            dataClaimToUpdate={dataClaimToUpdate}
            isLawyer
            isCourtManager={props.isCourtManager}
            isOnlyForRead={props.isMonitoringManager}
          />
          <ClaimInformation
            isRecapPage
            isReadOnly
            isManager={userHasPrivilege(PRIVILEGES.ISCOURTMANAGER)}
            inputProps={{ disableUnderline: true }}
            disableInput
            inputLabelProps
            listings={props.listings}
            claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
            storedClaimToPreopen={props.claimRecap}
            stakeholders={props.stakeholders}
            setClaimRecap={props.setClaimRecap}
            setDataClaimToUpdate={setDataClaimToUpdate}
            dataClaimToUpdate={dataClaimToUpdate}
            isLawyer
            isCourtManager={props.isCourtManager}
            readInfoSagis={
              featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO) &&
              get(props.claimRecap, 'claim.status', '') ===
                CLAIM_STATUS.VALIDATED
            }
            policyInfo={props.policyInfo}
            sagisInfoClaim={props.sagisInfo}
          />
          {featureService.hasFeature(FEATURES.OPPOSING_PARTY) && (
            <OpposingPartyRecapContainer
              isRecapPage
              claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
              storedClaimToPreopen={props.claimRecap}
              listings={props.listings}
              requestUpdateVehicleInfo={props.requestUpdateVehicleInfo}
              policyInfo={props.policyInfo}
              setClaimRecap={props.setClaimRecap}
              requestCreateVehicle={props.requestCreateVehicle}
              isLawyer
              isRejected={isRejected}
              setDataClaimToUpdate={setDataClaimToUpdate}
              dataClaimToUpdate={dataClaimToUpdate}
              claimId={props.claimId}
              isCourtManager={props.isCourtManager}
              sagisOpposingParties={extractOpposingParties(
                get(props.sagisInfo, 'offeredRisks', []),
              )}
            />
          )}
          {get(props.claimRecap, 'claim.status') !== CLAIM_STATUS.REJECTED &&
          !props.isCourtManager ? (
            <ClaimCircumstances
              isRecapPage
              isReadOnly
              isManager={false}
              inputProps={{ disableUnderline: true }}
              disableInput
              inputLabelProps
              listings={props.listings}
              claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
              storedClaimToPreopen={props.claimRecap}
              setClaimRecap={props.setClaimRecap}
              claimCircumstancesSagis={get(
                props.sagisInfo,
                'offeredRisks',
                [],
              )?.find(
                (element: { riskName: string }) =>
                  element.riskName === rcCoporeal,
              )}
            />
          ) : (
            <LawyerClaimCircumstances
              isRecapPage
              isReadOnly
              isManager={false}
              inputProps={{ disableUnderline: true }}
              inputLabelProps
              listings={props.listings}
              claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
              storedClaimToPreopen={props.claimRecap}
              setClaimRecap={props.setClaimRecap}
              setDataClaimToUpdate={setDataClaimToUpdate}
              dataClaimToUpdate={dataClaimToUpdate}
              isCourtManager={props.isCourtManager}
            />
          )}

          <VictimCard
            isRecapPage
            isReadOnly
            isManager={false}
            inputProps={{ disableUnderline: false }}
            disableInput
            inputLabelProps
            listings={props.listings}
            claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
            storedClaimToPreopen={props.claimRecap}
            requestUpdateVictimInfo={props.requestUpdateVictimInfo}
            requestUpdateBeneficiaryInfo={props.requestUpdateBeneficiaryInfo}
            policyInfo={props.policyInfo}
            requestCreateVictim={props.requestCreateVictim}
            setClaimRecap={props.setClaimRecap}
            isLawyer
            setDataClaimToUpdate={setDataClaimToUpdate}
            dataClaimToUpdate={dataClaimToUpdate}
            isCourtManager={props.isCourtManager}
            resetUploadedDocument={props.resetUploadedDocument}
            uploadedDocument={props.uploadedDocument}
            uploadDocument={props.uploadDocument}
            requestAddEligibilityTransaction={
              props.requestAddEligibilityTransaction
            }
            requestAddTransaction={props.requestAddTransaction}
            requestUpdateEligibilityTransaction={
              props.requestUpdateEligibilityTransaction
            }
            readInfoSagis={
              featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO) &&
              get(props.claimRecap, 'claim.status', '') ===
                CLAIM_STATUS.VALIDATED
            }
            sagisVictimsInfo={
              featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO)
                ? get(props.sagisInfo, 'victims', [])
                : []
            }
            transactionInboxeAreaId={props.transactionInboxeAreaId}
          />
          <UploadDocumentsBloc
            storedClaimToPreopen={props.claimRecap}
            isRecap
            isReadOnly
            isManager={false}
            index={0}
            document={supportDocument}
            setDocument={setDocument}
            resetUploadedDocument={props.resetUploadedDocument}
            uploadDocument={props.uploadDocument}
            uploadedDocument={props.uploadedDocument}
            setDocumentId={props.setDocumentId}
            docId={props.documentId}
            pvDocument={updatedPVDocument}
            setPVDocument={setPVDocument}
            isRejected={isRejected}
            setDataClaimToUpdate={setDataClaimToUpdate}
            dataClaimToUpdate={dataClaimToUpdate}
            isCourtManager={props.isCourtManager}
            requestGetReportsPolicy={props.requestGetReportsPolicy}
            requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
          />
          {featureService.hasFeature(FEATURES.FRAUDE_CORPO) && (
            <FraudeInformation
              isRecapPage
              updateFilde={setDataClaimToUpdate}
              claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
              storedClaimToPreopen={props.claimRecap}
              isLawyer
              isRejected={isRejected}
              setDataClaimToUpdate={setDataClaimToUpdate}
              dataClaimToUpdate={dataClaimToUpdate}
              listings={props.listings}
              claimRecap={props.claimRecap}
              setClaimRecap={props.setClaimRecap}
              requestUpdateClaim={props.requestUpdateClaim}
              claimId={props.claimId}
              requestUpdateFraudInfo={props.requestUpdateFraudInfo}
            />
          )}
          {((isRejected &&
            featureService.hasFeature(FEATURES.LAWYER_REJECTED_CLAIM_UPDATE)) ||
            (props.isCourtManager &&
              featureService.hasFeature(
                FEATURES.UPDATE_CLAIM_COURT_MANAGER,
              ))) && (
            <PrimaryButton
              background={BUTTON_COLORS.PRIMARY_BLUE}
              border={BUTTON_COLORS.PRIMARY_BLUE}
              titlecolor={BUTTON_COLORS.BLUE_BUTTON_TITLE}
              title={translate(messages.validationButtonTitle, props)}
              onClick={handleClickUpdateData}
              className="validationButton"
            />
          )}
        </Container>
      )}
    </GlobalBloc>
  );
};

export default injectIntl(LawyerRecap);
